
import { defineComponent } from 'vue';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, alertController } from '@ionic/vue';
import { arrowBackOutline } from 'ionicons/icons';
import { leadApi, loadMeta, toLocalInput, optionDefault } from '../../salesLeads';
import { Deal, DealProduct, emptyDeal, dealFromSaved, dealPayload } from '../../deals';
import DealLines from './DealLines.vue';

const EMPTY = {
  company_name: '', contact_name: '', contact_designation: '', contact_phone: '', contact_email: '', address: '', port_id: '',
  owner_id: '', source: '', source_detail: '', segment: '', priority: 'Warm', expected_close_date: '', next_follow_up_at: '', remarks: '',
};
// Lookup fields that may fill this form (never private client data: the server does not send it).
const FILLABLE = ['contact_name', 'contact_designation', 'company_name', 'address', 'contact_email', 'port_id', 'segment', 'source_detail'];

export default defineComponent({
  name: 'SalesLeadForm',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, IonSpinner, DealLines },
  data() {
    return {
      meta: null as any,
      form: { ...EMPTY } as Record<string, any>,
      deal: emptyDeal() as Deal,
      found: null as any,
      lookedUp: '',
      duplicate: null as string | null,
      saving: false,
      arrowBackOutline,
    };
  },
  computed: {
    leadId(): string | null {
      return (this.$route.params.leadId as string) || null;
    },
    products(): DealProduct[] {
      return this.meta && this.meta.products ? this.meta.products : [];
    },
  },
  async created() {
    this.meta = await loadMeta().catch(() => null);
    if (!this.leadId) {
      this.form.priority = optionDefault(this.meta, 'priority');
      this.deal = emptyDeal(this.products);
      return;
    }
    leadApi('get', { lead_id: this.leadId }).then(response => {
      const lead = response.data.lead;
      for (const key of Object.keys(EMPTY)) {
        if (lead[key] !== undefined && lead[key] !== null) {
          this.form[key] = lead[key];
        }
      }
      this.form.next_follow_up_at = toLocalInput(lead.next_follow_up_at);
      this.form.expected_close_date = lead.expected_close_date || '';
      this.deal = dealFromSaved(lead, this.products);
      this.lookedUp = String(lead.contact_phone || '');
    }).catch(() => undefined);
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    async alert(header: string, message: string) {
      const a = await alertController.create({ header, message, buttons: ['OK'] });
      await a.present();
    },
    // Phone first: an existing client (or an earlier lead) fills the form. A new lead takes every
    // found detail; when editing, only empty fields are filled.
    lookup() {
      const phone = String(this.form.contact_phone || '');
      if (phone.replace(/\D/g, '').length < 6 || phone === this.lookedUp) {
        return;
      }
      this.lookedUp = phone;
      leadApi('lookupPhone', { phone }, true).then(response => {
        this.found = response.data;
        const fill = response.data.fill || {};
        for (const key of FILLABLE) {
          if (fill[key] !== undefined && fill[key] !== null && (!this.leadId || !this.form[key])) {
            this.form[key] = fill[key];
          }
        }
        this.checkDuplicate();
      }).catch(() => undefined);
    },
    // Warns before submit; the server enforces the same rule (one open lead per client per product) on save.
    checkDuplicate() {
      if (!this.form.contact_phone && !this.form.company_name) {
        return;
      }
      leadApi('checkDuplicate', {
        contact_phone: this.form.contact_phone, company_name: this.form.company_name, lead_id: this.leadId || '',
        jahaji_product_id: this.deal.jahaji_product_id,
      }, true).then(response => {
        this.duplicate = response.data.blocked ? response.data.message : null;
      }).catch(() => undefined);
    },
    save() {
      for (const [field, name] of [['contact_phone', 'Phone'], ['company_name', 'Company name'], ['contact_name', 'Contact person'], ['source', 'Source of sale']]) {
        if (!String(this.form[field] || '').trim()) {
          this.alert('Required', name + ' is required.');
          return;
        }
      }
      if (!this.deal.jahaji_product_id) {
        this.alert('Required', 'Choose a product.');
        return;
      }
      if (this.duplicate) {
        this.alert('Duplicate lead', this.duplicate);
        return;
      }
      this.saving = true;
      const payload: Record<string, any> = { ...this.form, ...dealPayload(this.deal) };
      if (this.leadId) {
        payload.lead_id = this.leadId;
        delete payload.owner_id;
      }
      leadApi(this.leadId ? 'update' : 'create', payload).then(response => {
        this.saving = false;
        const id = this.leadId || response.data.lead_id;
        this.$router.replace('/sales-leads/' + id);
      }).catch(() => {
        this.saving = false;
      });
    },
  },
});
